/**
 * Shared `head()` builders for hub pages so every hub route (English and
 * Finnish, index and child) emits the same canonical/hreflang/JSON-LD shape.
 */
import { buildHreflangLinks, buildLocaleMeta, SITE_ORIGIN, type SiteLocale } from "@/lib/i18n-paths";

type AnyLoader = any;

export function hubIndexHead(opts: {
  path: string;
  locale: SiteLocale;
  loaderData: AnyLoader;
  fallbackTitle: string;
  fallbackDescription: string;
  listName: string;
}) {
  const hub = opts.loaderData?.hub;
  const places = (opts.loaderData?.places ?? []) as { name: string; slug: string }[];
  const url = `${SITE_ORIGIN}${opts.path}`;
  const title = (opts.locale === "fi" ? null : hub?.seo_title) || opts.fallbackTitle;
  const desc = (opts.locale === "fi" ? null : hub?.seo_description) || opts.fallbackDescription;
  return {
    meta: [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      ...buildLocaleMeta(opts.locale),
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(opts.path)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: opts.listName,
          url,
          numberOfItems: places.length,
          itemListElement: places.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.name,
            url: `${url}/${p.slug}`,
          })),
        }),
      },
    ],
  };
}

export function hubPlaceHead(opts: { path: string; locale: SiteLocale; loaderData: AnyLoader }) {
  if (!opts.loaderData) {
    return {
      meta: [{ title: "Page not found — what Finland" }, { name: "robots", content: "noindex" }],
    };
  }
  const place = opts.loaderData.place;
  const url = `${SITE_ORIGIN}${opts.path}`;
  const title =
    opts.locale === "fi" ? `${place.name} — opas | what Finland` : `${place.name} — visitor guide | what Finland`;
  const desc =
    (place.description ?? "").slice(0, 155) ||
    (opts.locale === "fi" ? `Vieraile kohteessa ${place.name}.` : `Plan your visit to ${place.name} in Finland.`);
  return {
    meta: [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      ...buildLocaleMeta(opts.locale),
      { name: "twitter:card", content: "summary_large_image" },
      ...(place.image_url?.startsWith("https://")
        ? [
            { property: "og:image", content: place.image_url },
            { name: "twitter:image", content: place.image_url },
          ]
        : []),
    ],
    links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(opts.path)],
  };
}
